import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import QRCodeLib from 'qrcode';
import { Share2, Check, MessageCircle, Send, Mail, Link as LinkIcon, X as XIcon, Facebook, QrCode, ArrowLeft, ImageIcon, FileText } from 'lucide-react';

// Public URL of the shareable poster (1.5 MB PNG). Served straight from
// /public so social platforms can OG-scrape a real absolute URL when the
// share message includes it.
const POSTER_PATH = '/share-poster.png';
const POSTER_ABSOLUTE = (typeof window !== 'undefined')
  ? `${window.location.origin}${POSTER_PATH}`
  : `https://zakat.international${POSTER_PATH}`;

const SHARE_WRITEUP =
  'Amanah by Zakat.International — free, private calculators for Zakat, Islamic inheritance (Faraid) and Wasiyyah (will). Available in 15 languages across 95+ countries. Nothing you enter leaves your browser.';

/**
 * Compact share icon for the top header. Renders a small ghost circle button
 * that opens a popover with the six most-used share targets:
 *
 *   • WhatsApp      • Telegram
 *   • X / Twitter   • Facebook
 *   • Email         • Copy link
 *
 * No external state — the popover is self-contained, dismisses on outside
 * click / Esc / scroll, and uses the current window.location as the share
 * URL. Mobile devices fall through to the native share sheet via the
 * Web Share API when available (single tap on the icon).
 *
 * Props:
 *   • title (optional) — share title; falls back to <document.title>
 *   • testid (optional) — base data-testid prefix, default 'header-share'
 *   • align (optional) — 'right' | 'left'. Default 'right' (popover opens
 *     to the left of the trigger so it stays in-viewport on small screens).
 */
export const HeaderShareButton = ({ title, testid = 'header-share', align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const rootRef = useRef(null);

  const shareTitle = title || (typeof document !== 'undefined' ? document.title : 'Amanah by Zakat.International');
  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://zakat.international';

  // Rich share text: writeup + link + poster URL so recipients see a real
  // preview on WhatsApp / Telegram / Twitter (OG scraping) AND get a direct
  // image URL to save.
  const shareText = `${SHARE_WRITEUP}\n\n${shareUrl}\n\nPoster: ${POSTER_ABSOLUTE}`;

  const openTarget = useCallback((url) => {
    setOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleNative = useCallback(async () => {
    if (!navigator.share) {
      setOpen((v) => !v);
      return;
    }
    // Try to attach the poster as a File so the OS share sheet can hand it
    // to WhatsApp / iMessage / Photos etc as an actual image, not just a URL.
    try {
      let files;
      try {
        const res = await fetch(POSTER_PATH);
        const blob = await res.blob();
        const file = new File([blob], 'zakat-international-poster.png', { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          files = [file];
        }
      } catch (_e) { /* fallback to URL-only share */ }
      await navigator.share(
        files
          ? { title: shareTitle, text: `${SHARE_WRITEUP}\n\n${shareUrl}`, files }
          : { title: shareTitle, text: `${SHARE_WRITEUP}\n\n${shareUrl}\n\nPoster: ${POSTER_ABSOLUTE}`, url: shareUrl }
      );
    } catch {
      // user cancelled — open the manual popover as a fallback
      setOpen(true);
    }
  }, [shareTitle, shareUrl]);

  const downloadPoster = useCallback(async () => {
    const a = document.createElement('a');
    a.href = POSTER_PATH;
    a.download = 'zakat-international-poster.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  // Download the A4 masjid poster (PDF, generated on-device with jsPDF).
  // Wrapped in a lazy import so the ~130 KB jsPDF bundle stays out of the
  // main chunk until a user actually asks for a PDF.
  const downloadPosterPdf = useCallback(async () => {
    try {
      const [{ downloadMasjidPosterPdf, PRESET_HADITHS }] = await Promise.all([
        import('../utils/masjidPoster'),
      ]);
      await downloadMasjidPosterPdf({
        masjidName: '',
        hadith: PRESET_HADITHS[0],
        richMode: true,
      });
    } catch (e) {
      // Silent fail — the PNG download is still available.
    }
  }, []);

  const copyLink = useCallback(async () => {
    // Copy the FULL rich payload — writeup + site URL + poster URL —
    // so pasting into any platform gives recipients the full context.
    const rich = `${SHARE_WRITEUP}\n\n${shareUrl}\n\nPoster: ${POSTER_ABSOLUTE}`;
    try {
      await navigator.clipboard.writeText(rich);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, 1200);
    } catch {
      try { window.prompt('Copy this link:', shareUrl); } catch { /* ignore */ }
    }
  }, [shareUrl]);

  const downloadQR = useCallback(async () => {
    try {
      // Render a fresh 512×512 PNG data-URL so the download is high-res on
      // print / social handoff — larger than the on-screen SVG preview.
      const dataUrl = await QRCodeLib.toDataURL(shareUrl, {
        margin: 2,
        width: 512,
        errorCorrectionLevel: 'M',
        color: { dark: '#0D1520', light: '#ffffff' },
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `zakat-international-qr-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      // Silent fail — the user still has the on-screen QR to screenshot.
    }
  }, [shareUrl]);

  // Reset QR sub-panel whenever the popover closes.
  useEffect(() => { if (!open) setShowQR(false); }, [open]);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const items = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      Icon: MessageCircle,
      tone: 'text-[#0D1520] hover:bg-[#F3EFEA]',
      onClick: () => openTarget(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`),
    },
    {
      key: 'telegram',
      label: 'Telegram',
      Icon: Send,
      tone: 'text-sky-700 hover:bg-sky-50',
      onClick: () => openTarget(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`),
    },
    {
      key: 'x',
      label: 'X / Twitter',
      Icon: XIcon,
      tone: 'text-[#0D1520] hover:bg-stone-100',
      onClick: () => openTarget(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`),
    },
    {
      key: 'facebook',
      label: 'Facebook',
      Icon: Facebook,
      tone: 'text-blue-700 hover:bg-blue-50',
      onClick: () => openTarget(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`),
    },
    {
      key: 'email',
      label: 'Email',
      Icon: Mail,
      tone: 'text-amber-700 hover:bg-amber-50',
      onClick: () => openTarget(`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText)}`),
    },
    {
      key: 'qr',
      label: 'QR code',
      Icon: QrCode,
      tone: 'text-[#0D1520] hover:bg-[#F3EFEA]',
      onClick: () => setShowQR(true),
    },
    {
      key: 'poster',
      label: 'Download poster',
      Icon: ImageIcon,
      tone: 'text-[#4A5568] hover:bg-[#F3EFEA]',
      onClick: () => { downloadPoster(); setOpen(false); },
    },
    {
      key: 'copy',
      label: copied ? 'Copied!' : 'Copy link + poster',
      Icon: copied ? Check : LinkIcon,
      tone: copied ? 'text-[#0D4733] bg-[#F3EFEA]' : 'text-[#0D4733] hover:bg-[#F3EFEA]',
      onClick: copyLink,
    },
  ];

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        onClick={handleNative}
        data-testid={`${testid}-trigger`}
        aria-label="Share this page"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Share"
        className="inline-flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-gold-500/95 border border-gold-400 text-[#0D1520] hover:bg-gold-400 hover:border-gold-300 shadow-sm ring-1 ring-gold-300/40 transition-colors"
      >
        <Share2 className="w-4 h-4" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Share options"
          data-testid={`${testid}-popover`}
          className={`absolute top-11 z-[60] w-56 rounded-xl border border-stone-200 bg-white shadow-xl p-2 animate-in fade-in slide-in-from-top-1 ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {!showQR && (
            <>
              <div className="px-2 pb-1.5 pt-0.5 text-[10px] font-mono uppercase tracking-wider text-stone-500">
                Share Amanah by Zakat.International
              </div>
              <ul className="space-y-0.5">
                {items.map(({ key, label, Icon, tone, onClick }) => (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={onClick}
                      data-testid={`${testid}-${key}`}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${tone}`}
                    >
                      <span className="shrink-0 w-7 h-7 rounded-full bg-current/10 flex items-center justify-center">
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span>{label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {showQR && (
            <div data-testid={`${testid}-qr-panel`} className="p-1">
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={() => setShowQR(false)}
                  data-testid={`${testid}-qr-back`}
                  className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-[#4A5568]"
                >
                  <ArrowLeft className="w-3 h-3" /> Back
                </button>
                <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">Scan or share</span>
              </div>
              <div className="flex justify-center rounded-lg border border-stone-200 bg-white p-3">
                <QRCodeSVG
                  value={shareUrl}
                  size={168}
                  level="M"
                  fgColor="#0D1520"
                  bgColor="#ffffff"
                  data-testid={`${testid}-qr-svg`}
                />
              </div>
              <p className="mt-2 px-1 text-[10.5px] text-stone-500 leading-snug break-all italic">
                {shareUrl}
              </p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={downloadQR}
                  data-testid={`${testid}-qr-download`}
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-[#EDF5F1] text-[#0D4733] hover:bg-[#EDF5F1] text-[12px] font-medium"
                >
                  <QrCode className="w-3.5 h-3.5" /> QR
                </button>
                <button
                  type="button"
                  onClick={downloadPoster}
                  data-testid={`${testid}-poster-download`}
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-gold-500 text-[#0D1520] hover:bg-gold-400 text-[12px] font-medium"
                >
                  <ImageIcon className="w-3.5 h-3.5" /> PNG
                </button>
                <button
                  type="button"
                  onClick={downloadPosterPdf}
                  data-testid={`${testid}-poster-pdf`}
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-[#EDF5F1] text-[#0D4733] hover:bg-[#EDF5F1] text-[12px] font-medium"
                >
                  <FileText className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
              {/* Poster thumbnail — subtle proof of what will download */}
              <img
                src={POSTER_PATH}
                alt="Amanah by Zakat.International share poster"
                data-testid={`${testid}-poster-thumb`}
                loading="lazy"
                decoding="async"
                width="1200"
                height="630"
                className="mt-2 w-full rounded-lg border border-stone-200 object-cover"
                style={{ maxHeight: '80px' }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderShareButton;
